import { supabase } from '../utils/supabase';
import { UserRole } from './invitationService';
import { Organization } from '../types';

export interface UserData {
  id: string;
  email: string;
  name?: string;
  role: string;
  created_at: string;
}

interface ListUsersParams {
  organizationId: string;
  page?: number;
  itemsPerPage?: number;
}

interface ListUsersResponse {
  success: boolean;
  data: UserData[];
  count: number;
  error?: string;
}

interface RemoveUserParams {
  userId: string;
  organizationId: string;
}

interface RemoveUserResponse {
  success: boolean;
  error?: string;
}

interface ChangeUserRoleParams {
  userId: string;
  organizationId: string;
  newRole: UserRole;
}

interface ChangeUserRoleResponse {
  success: boolean;
  error?: string;
}

export class UserOrganizationService {
  /**
   * Fetch all organizations for a user
   */
  static async getUserOrganizations(userId: string): Promise<Organization[]> {
    if (!userId) {
      return [];
    }

    try {
      const { data: userOrgs, error } = await supabase
        .from('user_organizations')
        .select(
          `
          organization_id,
          role,
          organizations (
            id,
            name,
            slogan,
            slug,
            logo_url,
            settings
          )
        `
        )
        .eq('user_id', userId);

      if (error) {
        console.error('Error fetching user organizations:', error);
        throw error;
      }

      const organizations: Organization[] =
        userOrgs?.map((uo: any) => ({
          id: uo.organizations?.id,
          name: uo.organizations?.name,
          slogan: uo.organizations?.slogan,
          slug: uo.organizations?.slug,
          settings: uo.organizations?.settings,
          role: uo.role as UserRole,
          logo_url: uo.organizations?.logo_url
        })) || [];

      return organizations;
    } catch (error) {
      console.error('Error fetching user organizations:', error);
      throw error;
    }
  }

  static async listUsers({
    organizationId,
    page = 1,
    itemsPerPage = 10
  }: ListUsersParams): Promise<ListUsersResponse> {
    if (!organizationId) {
      return {
        success: false,
        data: [],
        count: 0,
        error: 'Organization ID is required'
      };
    }

    try {
      const { data, error } = await supabase
        .from('user_organizations')
        .select(
          `
          role,
          users!inner (
            id,
            email,
            name,
            created_at
          )
        `
        )
        .eq('organization_id', organizationId);

      if (error) {
        throw new Error(error.message);
      }

      const mappedUsers = data.map(record => {
        const user = Array.isArray(record.users) ? record.users[0] : record.users;
        return {
          id: user.id,
          email: user.email,
          name: user.name,
          role: record.role,
          created_at: user.created_at
        };
      });

      return {
        success: true,
        data: mappedUsers,
        count: mappedUsers.length
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        count: 0,
        error: (error as Error).message
      };
    }
  }

  static async removeUser({
    userId,
    organizationId
  }: RemoveUserParams): Promise<RemoveUserResponse> {
    if (!userId || !organizationId) {
      return {
        success: false,
        error: 'User ID and organization ID are required'
      };
    }

    try {
      const { error } = await supabase
        .from('user_organizations')
        .delete()
        .eq('user_id', userId)
        .eq('organization_id', organizationId);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: (error as Error).message
      };
    }
  }

  static async changeUserRole({
    userId,
    organizationId,
    newRole
  }: ChangeUserRoleParams): Promise<ChangeUserRoleResponse> {
    if (!userId || !organizationId || !newRole) {
      return {
        success: false,
        error: 'User ID, organization ID, and new role are required'
      };
    }

    try {
      const { error } = await supabase
        .from('user_organizations')
        .update({ role: newRole })
        .eq('user_id', userId)
        .eq('organization_id', organizationId);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: (error as Error).message
      };
    }
  }
}
